import { apiClient, HttpError } from '@littlebird/auth-client';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import {
  AssistantFileSchema,
  CatalogueEntrySchema,
} from '@/features/assistant-files/assistant-files-query';

const RoutineMemorySchema = z.object({
  notes_root: z.string().nullable(),
  assistant_notes: z.string(),
  files: z.array(CatalogueEntrySchema),
});

export type RoutineMemory = z.infer<typeof RoutineMemorySchema>;

const MAX_RETRIES = 3;

/** A deleted routine or file will not come back, so a 404 fails at once */
class NotFoundError extends Error {}

const retryUnlessNotFound = (failureCount: number, error: Error) =>
  !(error instanceof NotFoundError) && failureCount < MAX_RETRIES;

const ROUTINE_MEMORY_KEYS = {
  all: (routineId: number) => ['routine-memory', routineId] as const,
  file: (routineId: number, path: string) =>
    [...ROUTINE_MEMORY_KEYS.all(routineId), 'file', path] as const,
};

export const getRoutineMemoryQuery = (routineId: number) =>
  queryOptions({
    queryKey: ROUTINE_MEMORY_KEYS.all(routineId),
    staleTime: 0,
    refetchOnMount: 'always',
    retry: retryUnlessNotFound,
    queryFn: async ({ signal }) => {
      try {
        const data = await apiClient.get(
          `report_templates/${routineId}/memory`,
          { signal }
        );
        return RoutineMemorySchema.parse(data);
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
          throw new NotFoundError('Routine not found');
        }
        throw error instanceof HttpError
          ? new Error('Failed to fetch routine memory')
          : error;
      }
    },
  });

export const getRoutineMemoryFileQuery = (
  routineId: number,
  path: string | null
) =>
  queryOptions({
    queryKey: ROUTINE_MEMORY_KEYS.file(routineId, path ?? ''),
    enabled: !!path,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: retryUnlessNotFound,
    queryFn: async ({ signal }) => {
      if (!path) {
        throw new Error('No file path selected');
      }
      try {
        const url = `report_templates/${routineId}/memory/file?path=${encodeURIComponent(path)}`;
        const data = await apiClient.get(url, { signal });
        return AssistantFileSchema.parse(data);
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
          // AssistantFilesViewer shows a specific message for this text
          throw new NotFoundError('File not found');
        }
        throw error instanceof HttpError
          ? new Error('Failed to fetch routine memory file')
          : error;
      }
    },
  });
